import type { StreamingChatMessage } from '../../hooks/useStreamingMissionChat'
import { currentRunCarried } from './followUpTranscript'

/**
 * The stored record of a run this chat launched, once the run has ended
 * (agion/Agion#2871). The launching chat shows only what its live stream
 * delivered; a stream that stopped delivering events left the run's answer
 * off the screen although the Edge host stored it. When the run ends the chat
 * reads the stored host history and shows it in place of the live rows if it
 * holds conversation the screen lacks. A run whose stream delivered every
 * message keeps its live rows, so each message shows once.
 */

/** A conversation message's speaker and text, or null for activity rows. */
function conversationText(msg: StreamingChatMessage): string | null {
  const text = (value: unknown) => (typeof value === 'string' && value.trim() ? value.trim() : null)
  switch (msg.type) {
    case 'hitl_request': {
      const prompt = text(msg.data.prompt)
      return prompt ? `agent\u0000${prompt}` : null
    }
    case 'agent_turn': {
      const answer = text(msg.data.assistantResponse)
      return answer ? `agent\u0000${answer}` : null
    }
    case 'hitl_response': {
      const response = text(msg.data.response)
      return response ? `user\u0000${response}` : null
    }
    default:
      return null
  }
}

function countTexts(messages: readonly StreamingChatMessage[]): Map<string, number> {
  const counts = new Map<string, number>()
  for (const msg of messages) {
    const key = conversationText(msg)
    if (key) counts.set(key, (counts.get(key) ?? 0) + 1)
  }
  return counts
}

/** Whether the stored record holds a conversation message the screen does not show. */
export function storedRunHasMore(shown: readonly StreamingChatMessage[], stored: readonly StreamingChatMessage[]): boolean {
  const shownCounts = countTexts(shown)
  for (const [key, count] of countTexts(stored)) {
    if (count > (shownCounts.get(key) ?? 0)) return true
  }
  return false
}

/** Whether a stored user row is the history's copy of a message carried on screen. */
function echoesCarried(stored: StreamingChatMessage, carriedText: string): boolean {
  if (stored.type !== 'hitl_response' || typeof stored.data.response !== 'string') return false
  const text = stored.data.response.trim()
  // The host keeps a message with files as their note followed by its text.
  return text === carriedText || text.endsWith(`\n${carriedText}`)
}

function isRunEnding(msg: StreamingChatMessage): boolean {
  return msg.type === 'mission_completed' || msg.type === 'mission_failed'
}

/** Whether the stored record already holds the run's end. */
export function storedRunEnded(stored: readonly StreamingChatMessage[]): boolean {
  return stored.some(isRunEnding)
}

export interface StoredRunInput {
  /** The conversation carried on screen from earlier runs, with this run's launch message. */
  carried: readonly StreamingChatMessage[]
  /** The rows the live stream delivered for this run. */
  live: readonly StreamingChatMessage[]
  /** This run's stored host history, as the chat rebuilds it. */
  stored: readonly StreamingChatMessage[]
}

/**
 * This run's rows to show from its stored record, or null when the screen
 * already shows every conversation message the record holds. The record's
 * copy of the launch message carried on screen is left out, and a run end the
 * stream delivered stays when the record does not hold one yet.
 */
export function storedRunRows({ carried, live, stored }: StoredRunInput): StreamingChatMessage[] | null {
  const thisRunCarried = currentRunCarried(carried)
  if (!storedRunHasMore([...thisRunCarried, ...live], stored)) return null

  const rows = [...stored]
  for (const msg of thisRunCarried) {
    if (msg.type !== 'hitl_response' || typeof msg.data.response !== 'string' || !msg.data.response.trim()) continue
    const carriedText = msg.data.response.trim()
    const echo = rows.findIndex((row) => echoesCarried(row, carriedText))
    if (echo !== -1) rows.splice(echo, 1)
  }
  if (!rows.some(isRunEnding)) {
    const endings = live.filter(isRunEnding)
    const ending = endings[endings.length - 1]
    if (ending) rows.push(ending)
  }
  return rows
}
